import React, {
  type FunctionComponent,
  createContext,
  useContext,
  useMemo,
  useEffect,
} from 'react';
import {
  createInstance,
  Revenue,
  Identify,
} from '@amplitude/analytics-browser';
import type { Config, BrowserClient } from '@amplitude/analytics-types';

import {
  AMPLITUDE_API_KEY,
  AMPLITUDE_SERVER_URL,
  AMPLITUDE_STORAGE_KEY,
  APP_ENV,
} from '../../config';
import type { StringIndexedObject } from '../../types';
import { getIds } from './helpers';

export interface AmplitudeInstance extends BrowserClient {}

export interface AmplitudeProviderProps {
  children: JSX.Element | JSX.Element[];
  userId?: string;
  config?: Config;
}

export interface AmplitudeContextType {
  getParentContext?(): AmplitudeContextType;
  eventProperties?: StringIndexedObject;
  amplitude?: AmplitudeInstance;
}

export const amplitude = {
  ...createInstance(),
  Revenue,
  Identify,
};

function isValidAmplitudeInstance(instance?: AmplitudeInstance) {
  return (
    !!instance &&
    typeof instance.init === 'function' &&
    typeof instance.logEvent === 'function'
  );
}

export function initAmplitude(
  amplitude: AmplitudeInstance,
  userId?: string,
  config?: Config,
) {
  return () => {
    // If not a valid instance, return
    if (!isValidAmplitudeInstance(amplitude)) {
      if (APP_ENV === 'qa')
        console.log(
          'Invalid amplitude instance passed - cannot initialise Amplitude',
        );
      return;
    }
    // Try and get stored IDs from session storage
    const storedIds = window?.sessionStorage?.getItem(AMPLITUDE_STORAGE_KEY);
    // If storedIds not null, parse JSON string, else null
    const initIds = storedIds ? JSON.parse(storedIds) : null;
    // Initialise amplitude with props
    amplitude.init(AMPLITUDE_API_KEY, userId || undefined, {
      ...(config && { config }),
      serverUrl: AMPLITUDE_SERVER_URL,
      ...(initIds && { ...initIds }),
    });
  };
}

export const Amplitude = amplitude;

export const AmplitudeContext = createContext<AmplitudeContextType>({
  eventProperties: {},
  amplitude,
});

export function useAmplitudeContext() {
  return useContext(AmplitudeContext);
}

const AmplitudeProvider: FunctionComponent<AmplitudeProviderProps> = function ({
  children,
  userId,
  config,
}) {
  // Memoised to prevent init function getting rebuilt on every render
  const init = useMemo(
    () => initAmplitude(amplitude, userId, config),
    [amplitude, userId, config],
  );

  // Initialises amplitude
  init();

  // Effect stores the amplitude IDs for cross tab/cross session init
  useEffect(() => {
    // If amplitude not yet defined, return
    if (!amplitude) return;
    // Get IDs from amplitude object
    const ids = getIds(amplitude);
    // If no device ID, return
    if (!ids?.deviceId) return;
    // Set IDs in amplitude storage
    if (typeof window === 'undefined') return;
    window?.sessionStorage?.setItem(AMPLITUDE_STORAGE_KEY, JSON.stringify(ids));
  }, [amplitude]);

  return (
    <AmplitudeContext.Provider
      value={{
        eventProperties: {},
        amplitude,
      }}
    >
      {children}
    </AmplitudeContext.Provider>
  );
};

export default AmplitudeProvider;
